import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CampaignsService } from 'app/core/services/campaigns.service';
import {
  Campaign,
  CampaignFormModalData,
  CampaignFormModalResult,
  CampaignStatus,
  CAMPAIGN_DIALOG_PANEL_CLASS,
} from 'app/shared/models/campaign';
import { CampaignFormModalComponent } from '../campaign-form-modal/campaign-form-modal.component';

const TOAST_POSITION_CLASS = 'toast-top-full-width';

const FORM_MODAL_WIDTH_DESKTOP = '620px';
const FORM_MODAL_WIDTH_MOBILE = '95vw';
const DELETE_MODAL_WIDTH_DESKTOP = '470px';
const DELETE_MODAL_WIDTH_MOBILE = '90vw';
const MOBILE_BREAKPOINT_PX = 767;

@Component({
  selector: 'app-campaigns-list',
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './campaigns-list.component.html',
  styleUrls: ['./campaigns-list.component.scss'],
})
export class CampaignsListComponent implements OnInit, OnDestroy {
  private readonly campaignsService = inject(CampaignsService);
  private readonly dialog = inject(MatDialog);
  private readonly toastr = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  @ViewChild('deleteConfirmationTemplate')
  deleteConfirmationTemplate: TemplateRef<unknown>;

  campaigns: Campaign[] = [];
  filteredCampaigns: Campaign[] = [];
  searchTerm = '';
  isLoading = false;
  isDeleting = false;
  campaignPendingDeletion: Campaign | null = null;

  readonly campaignStatus = CampaignStatus;

  private deleteDialogRef: MatDialogRef<unknown> | null = null;
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadCampaigns();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEmpty(): boolean {
    return !this.isLoading && this.filteredCampaigns.length === 0;
  }

  loadCampaigns(): void {
    this.isLoading = true;

    this.campaignsService
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (campaigns) => {
          this.campaigns = campaigns;
          this.applyFilter();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.showError('support-campaigns.list.load-error');
        },
      });
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  openCreateModal(): void {
    this.openFormModal({ mode: 'create' });
  }

  openEditModal(campaign: Campaign): void {
    this.openFormModal({ mode: 'edit', campaign });
  }

  openViewModal(campaign: Campaign): void {
    this.openFormModal({ mode: 'view', campaign });
  }

  openDeleteConfirmation(campaign: Campaign): void {
    this.campaignPendingDeletion = campaign;

    let width = DELETE_MODAL_WIDTH_DESKTOP;

    if (this.isMobile()) {
      width = DELETE_MODAL_WIDTH_MOBILE;
    }

    this.deleteDialogRef = this.dialog.open(this.deleteConfirmationTemplate, {
      width,
      panelClass: CAMPAIGN_DIALOG_PANEL_CLASS,
      autoFocus: false,
    });

    this.deleteDialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.campaignPendingDeletion = null;
        this.deleteDialogRef = null;
      });
  }

  cancelDeletion(): void {
    this.deleteDialogRef?.close();
  }

  confirmDeletion(): void {
    const campaign = this.campaignPendingDeletion;

    if (!campaign || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.campaignsService
      .delete(campaign.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isDeleting = false;
          this.deleteDialogRef?.close();
          this.showSuccess('support-campaigns.list.toast-deleted');
          this.loadCampaigns();
        },
        error: () => {
          this.isDeleting = false;
          this.deleteDialogRef?.close();
          this.showError('support-campaigns.list.delete-error');
        },
      });
  }

  /** Resolves the i18n key for a status badge. */
  statusLabelKey(status: CampaignStatus): string {
    if (status === CampaignStatus.Active) {
      return 'support-campaigns.status.active';
    }

    if (status === CampaignStatus.Paused) {
      return 'support-campaigns.status.paused';
    }

    return 'support-campaigns.status.inactive';
  }

  private openFormModal(data: CampaignFormModalData): void {
    let width = FORM_MODAL_WIDTH_DESKTOP;

    if (this.isMobile()) {
      width = FORM_MODAL_WIDTH_MOBILE;
    }

    const dialogRef = this.dialog.open(CampaignFormModalComponent, {
      width,
      panelClass: CAMPAIGN_DIALOG_PANEL_CLASS,
      autoFocus: false,
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: CampaignFormModalResult | undefined) => {
        if (!result) {
          return;
        }

        if (result.mode === 'create') {
          this.showSuccess('support-campaigns.list.toast-created');
        } else {
          this.showSuccess('support-campaigns.list.toast-updated');
        }

        this.loadCampaigns();
      });
  }

  private applyFilter(): void {
    const normalizedTerm = this.searchTerm.trim().toLowerCase();

    this.filteredCampaigns = this.campaigns.filter((campaign) => {
      if (!normalizedTerm) {
        return true;
      }

      return campaign.title.toLowerCase().includes(normalizedTerm);
    });
  }

  private isMobile(): boolean {
    return window.innerWidth <= MOBILE_BREAKPOINT_PX;
  }

  private showSuccess(translationKey: string): void {
    const message = this.translateService.instant(translationKey);
    this.toastr.success(message, '', {
      positionClass: TOAST_POSITION_CLASS,
      disableTimeOut: false,
    });
  }

  private showError(translationKey: string): void {
    const message = this.translateService.instant(translationKey);
    this.toastr.error(message, '', {
      positionClass: TOAST_POSITION_CLASS,
      disableTimeOut: false,
    });
  }
}
